import { WspDistrictFilterKey } from "./wsp";

export type FilterKey = AgencyId | AgencyAbbr | WspDistrictFilterKey | JogId; // This type is for use throughout the code where the "ID" could be a value from more than one type.
export type AgencyId = string; // Should be a guid or simple digit (eventually only digits, and changed to number type)

export interface Agency {
  abbreviation: AgencyAbbr;
  id: AgencyId;
  category: AgencyCategory;
  createdBy: string; // email address
  createdDate: string;
  isActive?: boolean;
  isFake?: boolean;
  isFilter: boolean;
  isUserAgency: boolean;
  jogIds?: string[]; // The JOGs an Agency is a part of
  lastModifiedBy?: string; // email address
  lastModifiedDate?: string;
  name: string;
  // primaryContactEmail?: string;
  // primaryContactName?: string;
  // primaryContactPhoneNumber?: string;
  // primaryContactUserId?: string;
  shortName: string;
}

export interface Jog {
  agencies: Agency[];
  id: JogId;
  isFilter: boolean;
  isUserJog: boolean;
  isGroupOnly: boolean;
  name: string;
  shortName: string;
}

export enum AgencyCategory {
  City = "City",
  DOT = "Transportation",
  EmergencyMgmt = "Emergency Mgmt",
  Fire = "Fire",
  LawEnforcement = "Law Enforcement",
  PublicTransit = "Transit",
  PublicUtilities = "Public Utilities",
  PublicWorks = "Public Works",
  Other = "Other",
}

export enum JogId {
  // Not actual JOG
  NOT_SELECTED = "",
  Wsp = "WSPJog", // No events associated with this ID. Just used to group all Districts for dispatch event filter group.
  Other = "00",  // "No JOG"

  // Actual JOG or Group
  SeattleArea = "01", // "SEA"
  KingCountyNortheast = "02", // "NEJOG"
  KingCountySouth = "03", // "South JOG"
  Tacoma = "04", // "TAC"
  JBLM = "05",
  SnohomishCounty = "06",
  KitsapPeninsula = "07",
  ClarkCounty = "08",
  LowerColumbiaRiver = "11",
  NorthwestWashington = "17", // Includes SkagitCounty & WhatcomCounty.

  // Group, not actual JOG.
  EasternWashington = "18", // All area covered by WSP districts 3, 4, 6. Mainly added for IRT users in those areas.

  // Possible future JOGs
  LewisCounty = "10",
  Spokane = "13",
  TriCities = "15",

  // Obsolete - Leaving commented to note ID has been used.
  // CowlitzCounty = "09",  // Combined into Lower Columbia River JOG
  // ThurstonCounty = "14", // Not currently in progress.
  // SkagitCounty = "12",   // Replaced by NW WA JOG. 
  // WhatcomCounty = "16",  // Replaced by NW WA JOG.
}

// I'm not expecting to keep this in sync with all IDs in Dynamo.
// But I do need to reference the KCM and WSP ID, so I created it.
export enum AgencyIds {
  OTHER = "0",
  WSDOT = "b26bdc5c-c833-40cb-acfc-a3fccb560bb7", // 1
  WSP = "e83bda18-f3b9-42aa-959b-4dff695bf83f", // 2
  SeattlePD = "08b6c595-b878-48fe-860b-cb477ffc8212", // 3
  SeattleFD = "e43dc46a-ff45-4f01-a7c0-00adec1979f1", // 4
  KingCountyMetro = "b6086a8b-ac32-4970-b63c-87314318ff45", // 5
  SeatteDOT = "24e5abdc-bd35-426d-bcc1-bb4f1442badc", // 6
  SoundTransit = "8d264520-8114-4073-849a-7901880082ef", // 7
  PortOfSeattle = "35d38c0d-82a3-488a-924b-36089520362b", //8 
  NWSA = "553f1cac-aad2-4c99-8f42-9953463e28bc", // 9
  MercerIslandPD = "aa40cb52-1eca-4bd1-836f-85d532d8fd0c", // 10
  MedinaPD = "01512b80-e53d-4848-a305-5c9727eab6c3", // 11
  EastsideFire = "f2281dc8-f898-4a4b-844c-2dc639ea5207", // 12
}

// todo: split "future agency" Abbreviations into a different set?
export enum AgencyAbbr {
  NOT_SELECTED = "",
  OTHER = "Other",

  // Statewide / Multiple JOG
  WSP = "WSP",
  WSDOT = "WSDOT",
  NWSA = "NWSA", // Northwest Sea Port alliance
  KCM = "KCM",

  // SAJOG #################################  
  SDOT = "SDOT",
  SFD = "SFD",
  SPD = "SPD",
  PortOfSeattle = "Port",
  SeattleEmergencyManagement = "SEAOEM",
  SeattleDepartmentOfConstructionAndInspections = "SDCI",

  // NEJOG #################################
  CityOfBellevue = "COB",
  BellevueFire = "BELFD",
  BellevueDot = "BELDOT",
  BellevuePolice = "BELPD",
  BothellEmergencyMgmt = "BOTHEM",
  BothellPolice = "BOTHPD",
  EastsideFire = "EFR",
  FallCityFire = "FcFD",
  IssaquahFire = "IssaFD",
  IssaquahPublicWorks = "IssaPW",
  KingCountyEmergencyMgmt = "KingCoEM",
  KirklandFire = "KirkFD",
  KirklandPublicWorks = "KirkPW",
  MedinaPolice = "MDPD",
  MedinaPublicWorks = "MDPW",
  CityOfMercerIsland = "MI",
  MercerIslandPolice = "MIPD",
  CityOfNewcastle = "NC",
  NewcastlePublicWorks = "NCPW",
  Norcom = "NORCOM",
  RedmondFire = "REDFD",
  RedmondPolice = "REDPD",
  RedmondPublicWorks = "REDPW",
  ShorelineEmergencyMgmt = "SHOREM",
  ShorelineFire = "SHOREFD",
  SnoqualmieFire = "SNOFD",
  ST = "ST",
  //SoundTransitKC Metro/Link LightRail
  WaEmergencyMgmtDiv = "WAEM",

  // South King County JOG (South JOG) ##########
  AuburnPolice = "AUBPD",
  CityOfSeaTac = "CoST",
  CityOfTukwila = "CoTW",
  CovingtonPolice = "COVPD",
  DesMoinesPublicWorks = "DMPW",
  DesMoinesPolice = "DMPD",
  FederalWayEmergencyMgmt = "FWEM",
  FederalWayPolice = "FWPD",
  FederalWayPublicWorks = "FWPW",
  KingCountyMedicOne = "KCMO",
  KentPolice = "KENTPD",
  MapleValleyPolice = "MVPD",
  MapleValleyPublicWorks = "MVPW",
  MapleValleyEmergencyMgmt = "MVEM",
  MountainViewFireAndRescue = "MTVFR",
  PierceTransit = "PT",
  PugetSoundRegionalFireAuthority = "PSF",
  SeaTacEmergencyManagement = "STEM",
  SeaTacPublicWorks = "STPW",
  //Sound Transit
  //Sound Transit- KC Metro/Link Light Rail
  TukwilaEmergencyManagement = "TWEM",
  TukwilaPolice = "TWPD",
  TukwilaPublicWorks = "TWPW",
  ValleyRegionalFireAuthority = "VRFA",
  //WA Emergency Management Division

  // Tacoma JOG #################################
  BonneyLakeEmergencyManagementt = "BLEM",
  BonneyLakePublicWorks = "BLPW",
  BuckelyFire = "BuckleyFire",
  CentralPierceFireAndRescue = "CPFR",
  CityOfTacomaVenuesAndEvents = "TacVenEvents",
  EastPierceFireAndRescue = "EPFR",
  GigHarborFire = "GigFire",
  GigHarborPolice = "GigPD",
  LakewoodPolice = "LWPD",
  MiltonPublicWorks = "MiltonPW",
  // NWSA / Port of Tacoma/Port of Seattle
  OrtingEmergencyManagement = "OrtEM",
  OrtingPolice = "OrtPD",
  PierceCountyEmergencyMgmt = "PierceEM",
  PierceCountyPlanningAndPublicWorks = "PiercePW",
  PierceCountySheriffsOffice = "PierceSheriff",
  //PierceTransit = "PierceTransit", // Tacoma and South KC JOG
  PortOfTacoma = "POT",
  PuyallupEmergencyManagement = "PuyEM",
  PuyallupPolice = "PuyPD",
  PuyallupPublicWorks = "PuyPW",
  //Sound Transit
  SouthSound911 = "SouthSound911",
  SteilacoomPolice = "SMPD",
  SumnerEmergencyManagement = "SumEM",
  SumnerPublicWorks = "SumPW",
  TacomaFireOperations = "TacFireOps",
  TacomaFireEmergencyMngmt = "TacFireEM",
  TacomaPublicWorks = "TacPW",
  TacomaRail = "TacRail",
  UniversityPlacePolice = "UPPD",
  //WA Emergency Management Division
  WestPierceFireAndRescue = "WPFR",
  WestPierceFireAndRescueEmergancyMgmt = "WPFREM",

  // Snohomish County JOG ######################
  CityOfGoldBar = "GLD",
  CityOfSultan = "SUL",
  CityOfLakeStevens = "LKSTV",
  CityOfMarysville = "MARY",

  ArlingtonPolice = "ArlPD",
  // BothellPolice = "BothPD", // Also in NEJOG
  BrierPublicWorks = "BrierPW",
  CamanoIslandFireAndResue = "CIFR",
  CommunityTransit = "CommTransit",
  EdmondsPoplice = "EdPD",
  EdmondsPublicWorks = "EdPW",
  EverettFire = "EvFD",
  EverettPolice = "EvPD",
  EverettPublicWorks = "EvPW",
  GoldBarPolice = "GBPD",
  GoldBarPublicWorks = "GBPW",
  LakeStevensPolice = "LSPD",
  LakeStevensPublicWorks = "LSPW",
  LynnwoodPublicWorks = "LynnPW",
  MarysvilleFire = "MaryFD",
  MarysvillePolice = "MARYPD",
  MarysvillePublicWorks = "MaryPW",
  MonroePolice = "MonPD",
  MountlakeTerracePublicWorks = "MLTPW",
  MukilteoPolice = "MukPD",
  NorthCountyRegionalFireAuthority = "NoCoFR",
  SnoCoEmergencyManagement = "SnoEM",
  SnohomishRegionalFireAndRescue = "SRFR",
  SoundTransit = "ST",
  SouthCountyFire = "SoCoFire",
  StanwoodCamanoSchoolDistrict = "SCSD", // Also part of NW WA JOG
  StanwoodPublicWorks = "StanPW",
  StillaguamishPoplice = "StillPD",
  SultanPublicWorks = "SulPW",
  TulalipTribalPolice = "TulPD",
  //WA Emergency Management Division

  // Kitsap Peninsula JOG #######################
  BainbridgeIslandFire = "BIFD",
  BainbridgeIslandEmergencyManagement = "BIEM",
  BainbridgeIslandPolice = "BIPD",
  BremertonFire = "BIFD",
  BremertonPolice = "BremPD",
  BremertonPublicWorks = "BremPW",
  BrinnonFire = "BrinFD",
  CentralKitsapFireAndRescue = "CenKitFR",
  ClallamTransit = "ClAmTransit",
  EastJeffersonFireAndRescue = "EJFR",
  JeffersonCountyDeptOfEmergencyManagement = "JCEM",
  JeffersonCountySheriffsOffice = "JCSO",
  JeffersonTransitAuthority = "JTA",
  KitsapCountyDeptOfEmergencyManagement = "KitCoEM",
  KitsapCountySheriffsOffice = "KitCoSO",
  KitsapTransit = "KitTran",
  MACECOM911 = "MACECOM911",
  MasonCountyDeptOfEmergencyManagement = "MasCoEM",
  MasonCountyPublicWorks = "MasCoPW",
  MasonTransitAuthority = "MasTA",
  NavyRegionNorthwestFire = "NavyRegNWF",
  NavyShipyardEmergencyManagement = "NavySYEM",
  NorthKitsapFireAndRescue = "NKitFR",
  PortAngelesFire = "PAFD",
  PortGambleSKlallamTribeEmergencyManagement = "PGSTEM",
  PortOrchardPolice = "POPD",
  PortOrchardPublicWorks = "POPW",
  PortTownsendPolice = "PTPD",
  PoulsboEmergencyManagement = "PoulsEM",
  PoulsboFire = "PoulsFD",
  PoulsboPublicWorks = "PoulsPW",
  QuilceneFire = "QuilceneFD",
  SequimPolice = "SeqPD",
  SequimPublicWorks = "SeqPW",
  SheltonPublicWorks = "ShelPW",
  SkokomishTribalPolice = "SkoPD",
  SouthKitsapFireAndRescue = "SKitFR",

  // Lower Columbia River JOG
  CityOfLongview = "LV",
  WahkiakumCounty = "WAHK",

  // Northwest WA JOG
  SouthWhatcomFireAuthority = "SWFA",
  TownofLaConner = "TOLC",
  WhatcomCountyFireDistrict7 = "WCFD7",

  // Clark County JOG
  ClarkCountyFireDistrict3 = "CCFD3",

}

// Instead of using this in several places in the UI, I could try using it in the saga, where any field returned that could be an abbreviation is changed into an ID???
// that seems error prone. It would be better if we could do a data migration in Dynamo, probably.... hmm.
const AgencyIdConversionMap = new Map<AgencyAbbr, AgencyId>([
  [AgencyAbbr.WSDOT, AgencyIds.WSDOT],
  [AgencyAbbr.WSP, AgencyIds.WSP],
  [AgencyAbbr.SPD, AgencyIds.SeattlePD],
  [AgencyAbbr.SFD, AgencyIds.SeattleFD],
  [AgencyAbbr.KCM, AgencyIds.KingCountyMetro],
  [AgencyAbbr.SDOT, AgencyIds.SeatteDOT],
  [AgencyAbbr.ST, AgencyIds.SoundTransit],
  [AgencyAbbr.PortOfSeattle, AgencyIds.PortOfSeattle],
  [AgencyAbbr.NWSA, AgencyIds.NWSA],
  [AgencyAbbr.MercerIslandPolice, AgencyIds.MercerIslandPD],
  [AgencyAbbr.MedinaPolice, AgencyIds.MedinaPD],
  [AgencyAbbr.EastsideFire, AgencyIds.EastsideFire],
]);

/**
 * A utility for backwards compatibility. When an abbreviation is passed as an ID,
 * the applicable ID is returned instead when found, otherwise the original value is returned.
 * @param agencyId  A string that's an actual Agency ID or a "legacy ID" (Agency abbreviation or "code").
 * @returns 
 */
export const getAgencyId = (agencyId: AgencyId | AgencyAbbr): AgencyId => {
  // If we change dispatch processing to start populating "agency" with the agencyId,
  // this still works because that value will simply be returned when no match is 
  // found in the conversion map.
  return AgencyIdConversionMap.get(agencyId as AgencyAbbr) || agencyId;
}

/**
 * A utility for backwards compatibility. Think of this as "get agency safely." It works with Agency IDs and is backwards compatible with agency abbreviations.
 * 
 * @param agenciesById Usually the agenciesById object retreived from the AgenciesState, passed in as store.agenciesById or agencyStore.agenciesById
 * @param agencyId A string that's an actual Agency ID or a "legacy ID" (Agency abbreviation or "code").
 * @returns 
 */
// todo: refactor this into a function in the useAgencies hook
export const GetAgency = (agenciesById: Record<string, Agency>, agencyId: AgencyId | AgencyAbbr | undefined): Agency | undefined => {
  return agenciesById[getAgencyId(agencyId || "")];
}

/**
 * This works with Agency IDs, and is backwards compatible to work with agency abbreviations.
 * 
 * @param agenciesById Usually the agenciesById object retreived from the AgenciesState, passed in as store.agenciesById or agencyStore.agenciesById
 * @param agencyId A string that's an actual Agency ID or a "legacy ID" (Agency abbreviation or "code").
 * @returns 
 */
export const GetAgencyAbbr = (agenciesById: Record<string, Agency>, agencyId: AgencyId | AgencyAbbr): string => {
  return GetAgency(agenciesById, agencyId)?.abbreviation || agencyId;
}

// Values that are saved in the dispatch event areaCode/ac field by dispatch processing
export enum AreaCode {
  I5 = "I5",
  WSP_D1 = "D1",
  WSP_D2 = "D2",
  WSP_D3 = "D3",
  WSP_D4 = "D4",
  WSP_D5 = "D5",
  WSP_D6 = "D6",
  WSP_D7 = "D7",
  WSP_D8 = "D8",
}

// Values that are saved in the dispatch event regionCode/rc field by dispatch processing
export enum RegionCode {
  NW = "NW",    // Northwest
  SEA = "SEA",  // King County,
  TAC = "TAC",  // Thurston, Peirce counties,
  SW = "SW",    // Southwest
  NC = "NC",    // North Central
  S = "S",      // South Central and Southeast
  E = "E",      // East
  PEN = "PEN",  // Olympic Peninsula
}
